import { useState, useRef } from 'react'
import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { BusinessRef } from '@/types'
import { Eye, EyeOff, CalendarDays, Trash2, Upload, Scissors } from 'lucide-react'

type AdminMailDetail = {
  id: string
  sender: string
  subject: string | null
  received_on: string | null
  published: boolean
  published_at: string | null
  scanned: boolean
  scan_url: string | null
  shred_requested: boolean
  shred_request_notes: string | null
  shredded: boolean
  created_at: string
}

type Props = {
  business: BusinessRef
  physical_mail: AdminMailDetail
}

export default function BackofficeBusinessPhysicalMailsShow({ business, physical_mail: mail }: Props) {
  const confirm = useConfirm()
  const fileRef = useRef<HTMLInputElement>(null)
  const [scanUploading, setScanUploading] = useState(false)
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false)
  const [receiptDate, setReceiptDate] = useState(mail.received_on ?? '')

  function togglePublish() {
    const path = `/backoffice/businesses/${business.id}/physical_mails/${mail.id}/publication`
    if (mail.published) {
      router.delete(path)
    } else {
      router.post(path)
    }
  }

  function uploadScan(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setScanUploading(true)
    router.post(
      `/backoffice/businesses/${business.id}/physical_mails/${mail.id}/scan`,
      { scan: { file } },
      { forceFormData: true, onFinish: () => setScanUploading(false) }
    )
  }

  function submitReceiptDate() {
    if (!receiptDate) return
    router.post(`/backoffice/businesses/${business.id}/physical_mails/${mail.id}/receipt`, {
      receipt: { received_on: receiptDate },
    })
    setReceiptDialogOpen(false)
  }

  async function removeReceipt() {
    const ok = await confirm({
      title: 'Remove receipt date',
      description: 'Remove the receipt date for this mail?',
      confirmLabel: 'Remove',
      destructive: true,
    })
    if (ok) {
      router.delete(`/backoffice/businesses/${business.id}/physical_mails/${mail.id}/receipt`)
    }
  }

  async function shredMail() {
    const ok = await confirm({
      title: 'Confirm shredding',
      description: 'Mark this mail as shredded? This cannot be undone.',
      confirmLabel: 'Shred',
      destructive: true,
    })
    if (ok) {
      router.post(`/backoffice/businesses/${business.id}/physical_mails/${mail.id}/shredding`)
    }
  }

  async function deleteMail() {
    const ok = await confirm({
      title: 'Delete mail',
      description: `Delete this mail from ${mail.sender}? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (ok) {
      router.delete(`/backoffice/businesses/${business.id}/physical_mails/${mail.id}`)
    }
  }

  return (
    <BackofficeLayout
      title={`Mail from ${mail.sender}`}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name, href: `/backoffice/businesses/${business.id}` },
        { label: 'Physical Mail', href: `/backoffice/businesses/${business.id}/physical_mails` },
        { label: mail.sender },
      ]}
    >
      <div className="space-y-6 max-w-2xl">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{mail.sender}</h1>
            {mail.subject && <p className="text-sm text-muted-foreground mt-0.5">{mail.subject}</p>}
          </div>
          <div className="flex items-center gap-2">
            {mail.shredded ? (
              <Badge className="bg-destructive text-destructive-foreground font-normal">Shredded</Badge>
            ) : mail.shred_requested ? (
              <Badge className="bg-orange-600 text-white font-normal">Shred requested</Badge>
            ) : null}
            {mail.published ? (
              <Badge className="bg-green-600 text-white font-normal">Published</Badge>
            ) : (
              <Badge className="bg-secondary text-secondary-foreground font-normal">Hidden</Badge>
            )}
          </div>
        </div>

        {/* Details */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Details</h2>
          </div>
          <div className="divide-y">
            <div className="flex items-center px-4 py-3">
              <span className="text-xs text-muted-foreground w-32 shrink-0">Sender</span>
              <span className="text-sm text-foreground">{mail.sender}</span>
            </div>
            {mail.subject && (
              <div className="flex items-center px-4 py-3">
                <span className="text-xs text-muted-foreground w-32 shrink-0">Subject</span>
                <span className="text-sm text-foreground">{mail.subject}</span>
              </div>
            )}
            <div className="flex items-center px-4 py-3">
              <span className="text-xs text-muted-foreground w-32 shrink-0">Received on</span>
              <span className="text-sm text-foreground">{mail.received_on ?? 'Not logged'}</span>
            </div>
            <div className="flex items-center px-4 py-3">
              <span className="text-xs text-muted-foreground w-32 shrink-0">Scanned</span>
              <span className="text-sm text-foreground">{mail.scanned ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex items-center px-4 py-3">
              <span className="text-xs text-muted-foreground w-32 shrink-0">Uploaded</span>
              <span className="text-sm text-foreground">{new Date(mail.created_at).toLocaleDateString()}</span>
            </div>
            {mail.scan_url && (
              <div className="flex items-center px-4 py-3">
                <span className="text-xs text-muted-foreground w-32 shrink-0">Scan</span>
                <a
                  href={mail.scan_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  View scan →
                </a>
              </div>
            )}
            {mail.shred_request_notes && (
              <div className="flex items-center px-4 py-3">
                <span className="text-xs text-muted-foreground w-32 shrink-0">Shred notes</span>
                <span className="text-sm text-foreground">{mail.shred_request_notes}</span>
              </div>
            )}
          </div>
        </section>

        {/* Actions */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Actions</h2>
          </div>
          <div className="px-4 py-4 flex flex-wrap gap-3">
            {!mail.shredded && (
              <Button size="sm" variant="outline" onClick={togglePublish}>
                {mail.published ? (
                  <><EyeOff className="w-3.5 h-3.5 mr-1.5" />Unpublish</>
                ) : (
                  <><Eye className="w-3.5 h-3.5 mr-1.5" />Publish</>
                )}
              </Button>
            )}

            {!mail.received_on ? (
              <Button size="sm" variant="outline" onClick={() => setReceiptDialogOpen(true)}>
                <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                Log receipt date
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={removeReceipt}>
                Remove receipt date
              </Button>
            )}

            {!mail.scanned && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={scanUploading}
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {scanUploading ? 'Uploading…' : 'Upload scan'}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={uploadScan}
                />
              </>
            )}

            {mail.shred_requested && !mail.shredded && (
              <Button
                size="sm"
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={shredMail}
              >
                <Scissors className="w-3.5 h-3.5 mr-1.5" />
                Confirm shredding
              </Button>
            )}

            <Button
              size="sm"
              variant="outline"
              className="border-destructive/50 text-destructive hover:bg-destructive/10 ml-auto"
              onClick={deleteMail}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Delete mail
            </Button>
          </div>
        </section>
      </div>

      {/* Receipt date dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log receipt date</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="receipt-date">Date received</Label>
            <Input
              id="receipt-date"
              type="date"
              value={receiptDate}
              onChange={(e) => setReceiptDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitReceiptDate} disabled={!receiptDate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BackofficeLayout>
  )
}
